import { useOutletContext, Link } from 'react-router-dom';
import { useCommunityAdminStats } from '../../hooks/use-admin';
import {
  Users,
  MessageSquare,
  ShieldAlert,
  Eye,
  UserCheck,
  Crown,
  UserCog,
  Shield,
  Ban,
  Settings,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';

function StatCard({ title, value, icon: Icon, description, color = 'blue' }) {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/20',
    green: 'text-green-400 bg-green-500/20',
    yellow: 'text-yellow-400 bg-yellow-500/20',
    red: 'text-red-400 bg-red-500/20',
    purple: 'text-purple-400 bg-purple-500/20',
  };

  return (
    <Card className="bg-zinc-900/50 border-white/10 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <div className={`p-2 rounded-xl w-fit backdrop-blur-md ${colorClasses[color]} bg-opacity-10 border border-white/5`}>
          <Icon className="w-4 h-4" />
        </div>
        <CardTitle className="text-2xl font-bold text-white mt-2">{value}</CardTitle>
        <CardDescription className="text-sm text-zinc-400">
          {title}
        </CardDescription>
      </CardHeader>
      {description && (
        <CardContent>
          <p className="text-xs text-zinc-500">{description}</p>
        </CardContent>
      )}
    </Card>
  );
}

export default function CommunityAdminDashboard() {
  const { community } = useOutletContext();

  const { data: stats, isLoading: isStatsLoading } = useCommunityAdminStats(
    community?.oid
  );

  if (isStatsLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-zinc-800 rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="col-span-2 h-64 bg-zinc-800 rounded-lg"></div>
            <div className="col-span-2 h-64 bg-zinc-800 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  const {
    totalMembers = 0,
    totalDiscussions = 0,
    totalComments = 0,
    pinnedDiscussions = 0,
    frozenDiscussions = 0,
    recentActivity = {},
    membersByRole = {},
  } = stats?.stats || {};

  // Calculate totals for display
  const totalPosts = totalDiscussions;
  const totalReports = 0; // TODO: Add reports to backend

  return (
    <div className="p-8 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-zinc-400">Overview of {community.name}</p>
        </div>
        <Link to={`/forum/${community.slug}`}>
          <Button
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            View Community
          </Button>
        </Link>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Members"
          value={totalMembers.toLocaleString()}
          icon={Users}
          color="green"
          description="Members in your community"
        />
        <StatCard
          title="Total Discussions"
          value={totalPosts.toLocaleString()}
          icon={MessageSquare}
          color="blue"
          description={`${pinnedDiscussions} pinned, ${frozenDiscussions} frozen`}
        />
        <StatCard
          title="Total Comments"
          value={totalComments.toLocaleString()}
          icon={Eye}
          color="purple"
          description="Replies across all discussions"
        />
        <StatCard
          title="Open Reports"
          value={totalReports}
          icon={ShieldAlert}
          color={totalReports > 0 ? 'red' : 'green'}
          description="Reports requiring attention"
        />
      </div>

      {/* Team Overview */}
      <Card className="bg-zinc-900/50 border-white/10 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white">Team Overview</CardTitle>
          <CardDescription className="text-zinc-400">
            Community staff breakdown
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="relative overflow-hidden rounded-2xl p-4 bg-amber-500/10 border border-amber-500/20 backdrop-blur-sm hover:bg-amber-500/20 transition-all group">
              <div className="flex items-start justify-between mb-2">
                <span className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Owner</span>
                <div className="p-1.5 rounded-lg bg-zinc-900/50 text-amber-400 shadow-sm group-hover:scale-110 transition-transform">
                  <Crown className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">
                {membersByRole.owner || 0}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl p-4 bg-rose-500/10 border border-rose-500/20 backdrop-blur-sm hover:bg-rose-500/20 transition-all group">
              <div className="flex items-start justify-between mb-2">
                <span className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Super Admins</span>
                <div className="p-1.5 rounded-lg bg-zinc-900/50 text-rose-400 shadow-sm group-hover:scale-110 transition-transform">
                  <Crown className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">
                {membersByRole.superAdmin || 0}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl p-4 bg-violet-500/10 border border-violet-500/20 backdrop-blur-sm hover:bg-violet-500/20 transition-all group">
              <div className="flex items-start justify-between mb-2">
                <span className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Admins</span>
                <div className="p-1.5 rounded-lg bg-zinc-900/50 text-violet-400 shadow-sm group-hover:scale-110 transition-transform">
                  <UserCog className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">
                {membersByRole.admin || 0}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl p-4 bg-blue-500/10 border border-blue-500/20 backdrop-blur-sm hover:bg-blue-500/20 transition-all group">
              <div className="flex items-start justify-between mb-2">
                <span className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Moderators</span>
                <div className="p-1.5 rounded-lg bg-zinc-900/50 text-blue-400 shadow-sm group-hover:scale-110 transition-transform">
                  <Shield className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">
                {membersByRole.moderator || 0}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl p-4 bg-zinc-500/10 border border-zinc-500/20 backdrop-blur-sm hover:bg-zinc-500/20 transition-all group">
              <div className="flex items-start justify-between mb-2">
                <span className="text-zinc-400 text-xs font-medium uppercase tracking-wider">Members</span>
                <div className="p-1.5 rounded-lg bg-zinc-900/50 text-zinc-400 shadow-sm group-hover:scale-110 transition-transform">
                  <Users className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">
                {membersByRole.member || 0}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity Summary */}
      <Card className="bg-zinc-900/50 border-white/10 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white">Recent Activity (7 days)</CardTitle>
          <CardDescription className="text-zinc-400">
            What's happening in your community
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg">
              <div className="p-3 bg-blue-500/20 rounded-full">
                <MessageSquare className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {recentActivity.discussions || 0}
                </p>
                <p className="text-sm text-zinc-400">New discussions</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg">
              <div className="p-3 bg-green-500/20 rounded-full">
                <UserCheck className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {recentActivity.newMembers || 0}
                </p>
                <p className="text-sm text-zinc-400">New members</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-zinc-900/50 border-white/10 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-white">Quick Actions</CardTitle>
          <CardDescription className="text-zinc-400">
            Common administrative tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Link to={`/forum/${community.slug}/admin/members`}>
              <Button
                variant="outline"
                className="w-full h-auto py-6 flex-col border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
              >
                <Users className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Manage Members</span>
              </Button>
            </Link>
            <Link to={`/forum/${community.slug}/admin/discussions`}>
              <Button
                variant="outline"
                className="w-full h-auto py-6 flex-col border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
              >
                <MessageSquare className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Review Discussions</span>
              </Button>
            </Link>
            <Link to={`/forum/${community.slug}/admin/banned`}>
              <Button
                variant="outline"
                className="w-full h-auto py-6 flex-col border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
              >
                <Ban className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Banned Members</span>
              </Button>
            </Link>
            <Link to={`/forum/${community.slug}/admin/settings`}>
              <Button
                variant="outline"
                className="w-full h-auto py-6 flex-col border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
              >
                <Settings className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Settings</span>
              </Button>
            </Link>
          </div >
        </CardContent >
      </Card >
    </div >
  );
}
